import { parseAgentTimestamp } from '@/lib/api/agent-events';
import type { AgentActionStatus, AgentPendingAction } from '@/lib/api/types';

export interface ArgumentChange {
  key: string;
  from: string | null;
  to: string;
}

function show(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/**
 * What this change does, as before → after rows.
 *
 * Only the keys the change actually sets are listed: a `before` snapshot is the
 * whole entity (the workspace snapshot carries every plan limit), and showing
 * all of it would bury the one field being changed.
 */
export function diffArguments(
  before: Record<string, unknown> | null,
  args: Record<string, unknown>,
): ArgumentChange[] {
  return Object.entries(args).map(([key, value]) => {
    const previous = before && key in before ? show(before[key]) : null;
    return { key, from: previous === '' ? null : previous, to: show(value) };
  });
}

/**
 * The status to render, accounting for an expiry the server has not noticed.
 *
 * Expiry is evaluated lazily on the backend (on read and on apply), so a card
 * opened last night is still `pending` on the wire while Apply would refuse it.
 */
export function effectiveStatus(action: AgentPendingAction, now: Date = new Date()): AgentActionStatus {
  if (action.status !== 'pending') return action.status;
  const expiresAt = parseAgentTimestamp(action.expires_at);
  if (!expiresAt) return 'pending';
  return expiresAt.getTime() <= now.getTime() ? 'expired' : 'pending';
}

/** One line per terminal state. Apply can return any of the first four. */
export const ACTION_STATE_COPY: Record<Exclude<AgentActionStatus, 'pending'>, string> = {
  applied: 'Applied.',
  cancelled: 'Cancelled — nothing changed.',
  failed: 'Could not be applied.',
  stale: 'Not applied: this changed since it was proposed. Ask the assistant to look again.',
  expired: 'Expired — ask the assistant to propose it again.',
};
